import { officeErrorToLogObject, wordSyncWithLog } from "../utils/officeJsDebugLog";

/**
 * PrizeLogic double-bracket placeholders using Word **content controls** (tag = key).
 * Raw `[[KEY]]` body search is avoided for generation updates; see {@link applyTaggedPlaceholderDisplayValues}.
 */

const LOG_PREFIX = "[doubleBracketWord]";

async function ensureOfficeReady(): Promise<void> {
  if (typeof Office === "undefined") {
    throw new Error("Office.js is not loaded");
  }
  await Office.onReady();
}

const KEY_BODY_RE = /^[A-Za-z0-9_]+$/;

const toUpperTag = (fieldKey: string): string => fieldKey.trim().toUpperCase();

const isRangeTextSubstitutableContentControl = (cc: Word.ContentControl): boolean => {
  const typeStr = String(cc.type);
  return !(
    typeStr.includes("CheckBox") ||
    typeStr.includes("DropDownList") ||
    typeStr.includes("ComboBox") ||
    typeStr.includes("BuildingBlockGallery") ||
    typeStr.includes("DatePicker") ||
    typeStr.includes("Picture")
  );
};

const stylePlaceholderContentControl = (cc: Word.ContentControl, tag: string, placeholderInner: string): void => {
  cc.tag = tag;
  cc.title = tag;
  cc.placeholderText = `[[${placeholderInner}]]`;
  cc.cannotDelete = false;
  cc.cannotEdit = false;
  // Do not set `appearance` / `color` here: many Word hosts throw RichApi **GeneralException**
  // at `context.sync()` for those properties (Word on the web, older builds, or mixed API sets).
};

/** True if the plain story text contains `[[` that is not part of a well-formed `[[inner]]` pair. */
export function hasMalformedDoubleBracketPlaceholders(text: string): boolean {
  if (!text) {
    return false;
  }
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf("[[", i);
    if (start === -1) {
      return false;
    }
    const end = text.indexOf("]]", start + 2);
    if (end === -1) {
      return true;
    }
    const inner = text.slice(start + 2, end);
    if (!inner || /[\][]/.test(inner)) {
      return true;
    }
    i = end + 2;
  }
  return false;
}

export async function readDocumentBodyPlainText(): Promise<string> {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");
    await context.sync();
    return body.text ?? "";
  });
}

/**
 * Insert a plain-text content control at the selection: tag = key, placeholder hint `[[KEY]]`,
 * appearance bounding box, accent color.
 */
export const insertDoubleBracketPlaceholder = async (fieldKey: string): Promise<void> => {
  const tag = toUpperTag(fieldKey);
  if (!KEY_BODY_RE.test(fieldKey.trim())) {
    throw new Error("Field key must use only letters, digits, and underscores");
  }
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  await Word.run(async (context) => {
    const range = context.document.getSelection();
    const cc = range.insertContentControl(Word.ContentControlType.richText);
    stylePlaceholderContentControl(cc, tag, tag);
    cc.insertText("", Word.InsertLocation.replace);
    await wordSyncWithLog(context, LOG_PREFIX, "insertDoubleBracketPlaceholder:sync", { tag });
  });
};

/** Remove all content controls (and their text) for this tag, then any legacy raw `[[KEY]]` runs. */
export const removeDoubleBracketPlaceholdersForKey = async (fieldKey: string): Promise<void> => {
  const tag = toUpperTag(fieldKey);
  if (!KEY_BODY_RE.test(fieldKey.trim())) {
    return;
  }
  const token = `[[${tag}]]`;
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  await Word.run(async (context) => {
    const ccs = context.document.contentControls.getByTag(tag);
    ccs.load("items");
    await context.sync();
    for (let i = ccs.items.length - 1; i >= 0; i -= 1) {
      ccs.items[i].delete(false);
    }
    await context.sync();

    const body = context.document.body;
    let guard = 0;
    while (guard < 1000) {
      guard += 1;
      const results = body.search(token, {
        matchCase: true,
        matchWholeWord: false
      });
      context.load(results, "items");
      await context.sync();
      if (results.items.length === 0) {
        break;
      }
      results.items[0].delete();
      await context.sync();
    }
  });
};

export type MigrateBareDoubleBracketOptions = {
  readonly signal?: AbortSignal;
};

const MAX_MIGRATE_OCCURRENCES_PER_TAG = 64;

/**
 * If the search hit sits inside a content control already tagged with `upperTag`, it is the
 * placeholder text we just wrote (`[[TAG]]`), not a separate bare token — do not wrap again or
 * `body.search` will loop forever.
 */
async function rangeIsInsideSameTaggedPlaceholder(
  context: Word.RequestContext,
  range: Word.Range,
  upperTag: string
): Promise<boolean> {
  try {
    const parent = range.parentContentControl;
    parent.load(["tag", "isNullObject"]);
    await context.sync();
    if (parent.isNullObject) {
      return false;
    }
    const pTag = (parent.tag ?? "").trim().toUpperCase();
    return pTag === upperTag;
  } catch {
    return false;
  }
}

/**
 * Wrap bare `[[KEY]]` text in styled placeholder controls (longest keys first).
 * Call after opening a generation draft built from an older template.
 *
 * Uses **richText** content controls and one `Word.run` per match (see {@link migrateBracketPlaceholdersToContentControls}).
 */
export const migrateBareDoubleBracketTokensToContentControls = async (
  fieldKeys: readonly string[],
  options?: MigrateBareDoubleBracketOptions
): Promise<void> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  const signal = options?.signal;
  const throwIfAborted = (): void => {
    if (signal?.aborted) {
      throw new DOMException("Double-bracket migration aborted", "AbortError");
    }
  };

  const normalized = fieldKeys
    .map((k) => toUpperTag(k))
    .filter((k) => /^[A-Z0-9_]+$/.test(k));
  const sorted = [...new Set(normalized)].sort((a, b) => b.length - a.length);

  console.info(LOG_PREFIX, "migrateBareDoubleBracketTokensToContentControls start", {
    fieldKeyCount: fieldKeys.length,
    uniqueTags: sorted.length,
    tagsSample: sorted.slice(0, 24),
    tagsTruncated: sorted.length > 24
  });

  try {
    for (const tag of sorted) {
      throwIfAborted();
      const token = `[[${tag}]]`;
      let wrappedForTag = 0;
      for (let guard = 0; guard < MAX_MIGRATE_OCCURRENCES_PER_TAG; guard += 1) {
        throwIfAborted();
        if (guard > 0 && guard % 16 === 0) {
          console.info(LOG_PREFIX, "migrate: still running", { tag, guard });
        }
        const outcome = await Word.run(async (context) => {
          const body = context.document.body;
          const results = body.search(token, {
            matchCase: true,
            matchWholeWord: false
          });
          context.load(results, "items");
          await wordSyncWithLog(context, LOG_PREFIX, "migrate:search results load", { tag, guard });
          if (results.items.length === 0) {
            return "none" as const;
          }
          const range = results.items[0];
          if (await rangeIsInsideSameTaggedPlaceholder(context, range, tag)) {
            console.info(LOG_PREFIX, "migrate: stop — match is inside tagged placeholder (not bare token)", {
              tag,
              guard
            });
            return "none" as const;
          }
          const overlapping = range.contentControls;
          overlapping.load("items");
          await wordSyncWithLog(context, LOG_PREFIX, "migrate:overlap load", { tag, guard });
          if (overlapping.items.length > 0) {
            console.warn(LOG_PREFIX, "migrate: token overlaps content control(s); stop this tag", {
              tag,
              overlappingCount: overlapping.items.length
            });
            return "blocked" as const;
          }
          const cc = range.insertContentControl(Word.ContentControlType.richText);
          stylePlaceholderContentControl(cc, tag, tag);
          cc.insertText("", Word.InsertLocation.replace);
          await wordSyncWithLog(context, LOG_PREFIX, "migrate:after wrap token in CC", { tag, guard });
          return "wrapped" as const;
        });
        if (outcome === "none") {
          break;
        }
        if (outcome === "blocked") {
          break;
        }
        wrappedForTag += 1;
      }
      if (wrappedForTag > 0) {
        console.info(LOG_PREFIX, "migrate tag done", { tag, wrappedOccurrences: wrappedForTag });
      }
    }
    console.info(LOG_PREFIX, "migrateBareDoubleBracketTokensToContentControls complete");
  } catch (e) {
    console.error(LOG_PREFIX, "migrateBareDoubleBracketTokensToContentControls failed", officeErrorToLogObject(e));
    throw e;
  }
};

/**
 * Push display strings into tagged placeholder controls. Empty string → body text `[[TAG]]`.
 */
export const applyTaggedPlaceholderDisplayValues = async (
  displayByUpperTag: Record<string, string>
): Promise<void> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  const keys = Object.keys(displayByUpperTag).map((k) => k.trim().toUpperCase()).filter((k) => /^[A-Z0-9_]+$/.test(k));
  console.info(LOG_PREFIX, "applyTaggedPlaceholderDisplayValues start", {
    entryCount: Object.keys(displayByUpperTag).length,
    validTagCount: keys.length,
    tagsSample: keys.slice(0, 24)
  });
  try {
    await Word.run(async (context) => {
      for (const [rawTag, rawVal] of Object.entries(displayByUpperTag)) {
        const tag = rawTag.trim().toUpperCase();
        if (!/^[A-Z0-9_]+$/.test(tag)) {
          continue;
        }
        const trimmed = (rawVal ?? "").trim();
        const textInside = trimmed.length > 0 ? rawVal ?? "" : `[[${tag}]]`;
        let index = 0;
        let guard = 0;
        while (guard < 200) {
          guard += 1;
          const ccs = context.document.contentControls.getByTag(tag);
          ccs.load("items");
          await wordSyncWithLog(context, LOG_PREFIX, "apply:load CCs by tag", { tag, index });
          if (ccs.items.length === 0 || index >= ccs.items.length) {
            break;
          }
          const cc = ccs.items[index];
          cc.load("type");
          await wordSyncWithLog(context, LOG_PREFIX, "apply:load CC type", { tag, index });
          if (!isRangeTextSubstitutableContentControl(cc)) {
            if (import.meta.env.DEV) {
              console.warn(LOG_PREFIX, "apply: skip non-text content control", {
                tag,
                index,
                type: String(cc.type)
              });
            }
            index += 1;
            continue;
          }
          // Prefer control.insertText — getRange(whole).insertText on rich-text placeholders often
          // leaves the visible story unchanged when saving (Generate flow).
          cc.insertText(textInside, Word.InsertLocation.replace);
          await wordSyncWithLog(context, LOG_PREFIX, "apply:after insertText", { tag, index });
          index += 1;
        }
      }
    });
    console.info(LOG_PREFIX, "applyTaggedPlaceholderDisplayValues complete");
  } catch (e) {
    console.error(LOG_PREFIX, "applyTaggedPlaceholderDisplayValues failed", officeErrorToLogObject(e));
    throw e;
  }
};

/**
 * Remove placeholder wrappers for final save. `delete(true)` keeps inner content (Word JS API).
 */
export const unwrapTaggedPlaceholderControlsPreserveContent = async (
  tags: readonly string[]
): Promise<void> => {
  const sorted = [...new Set(tags.map((t) => toUpperTag(t)).filter((t) => /^[A-Z0-9_]+$/.test(t)))].sort(
    (a, b) => b.length - a.length
  );
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  console.info(LOG_PREFIX, "unwrapTaggedPlaceholderControlsPreserveContent start", {
    tagCount: sorted.length,
    tagsSample: sorted.slice(0, 24)
  });
  try {
    await Word.run(async (context) => {
      for (const tag of sorted) {
        for (let guard = 0; guard < 200; guard += 1) {
          const ccs = context.document.contentControls.getByTag(tag);
          ccs.load("items");
          await wordSyncWithLog(context, LOG_PREFIX, "unwrap:load CCs", { tag, guard });
          if (ccs.items.length === 0) {
            break;
          }
          const cc = ccs.items[ccs.items.length - 1];
          cc.delete(true);
          await wordSyncWithLog(context, LOG_PREFIX, "unwrap:after delete", { tag, guard });
        }
      }
    });
    console.info(LOG_PREFIX, "unwrapTaggedPlaceholderControlsPreserveContent complete");
  } catch (e) {
    console.error(LOG_PREFIX, "unwrapTaggedPlaceholderControlsPreserveContent failed", officeErrorToLogObject(e));
    throw e;
  }
};
